import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useBranches from "@/hooks/useBranches";
import { Tag, CheckCircle, MagicWand } from "@phosphor-icons/react";
import { fmtNumber } from "@/lib/format";

export default function BackfillDealerCodePanel() {
  const { branches = [] } = useBranches();
  const [selectedBranchIds, setSelectedBranchIds] = useState(() => new Set());
  const [dealerCode, setDealerCode] = useState("");
  const [onlyUnset, setOnlyUnset] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState(() => new Set());
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [inferPreview, setInferPreview] = useState(null);
  const [inferBusy, setInferBusy] = useState(false);

  const allSelected = useMemo(
    () => branches.length > 0 && branches.every((b) => selectedBranchIds.has(b.id)),
    [branches, selectedBranchIds],
  );

  // Reset cached preview whenever the inputs that drive the count change.
  useEffect(() => {
    setPreview(null);
    setLastResult(null);
  }, [selectedBranchIds, onlyUnset, selectedStatuses]);

  const toggle = (id) =>
    setSelectedBranchIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedBranchIds(allSelected ? new Set() : new Set(branches.map((b) => b.id)));

  const toggleStatus = (s) =>
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const runPreview = async () => {
    if (selectedBranchIds.size === 0) {
      toast.error("Select at least one branch first.");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/vehicles/admin/backfill-dealer-code/preview", {
        branch_ids: Array.from(selectedBranchIds),
        only_unset: onlyUnset,
        statuses: selectedStatuses.size ? Array.from(selectedStatuses) : null,
      });
      setPreview(data);
      if (data.matched_count === 0) {
        toast.info("No vehicles match — try turning off 'Only unset' or pick more branches.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const apply = async () => {
    if (!preview || preview.matched_count === 0) return;
    const code = Number(dealerCode);
    if (!code || code <= 0) {
      toast.error("Enter a positive numeric dealer code.");
      return;
    }
    if (!window.confirm(`Set dealer_code=${code} on ${preview.matched_count} vehicles?`)) {
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/vehicles/admin/backfill-dealer-code", {
        branch_ids: Array.from(selectedBranchIds),
        only_unset: onlyUnset,
        statuses: selectedStatuses.size ? Array.from(selectedStatuses) : null,
        dealer_code: code,
      });
      setLastResult(data);
      setPreview(null);
      toast.success(`Updated ${data.modified_count} vehicles with dealer_code ${data.dealer_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  const branchById = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);

  return (
    <div className="border-2 border-ink" data-testid="backfill-dealer-code-panel">
      <div className="px-4 py-3 border-b border-canvas-line flex items-center gap-2">
        <MagicWand size={18} weight="bold" className="text-ink" />
        <h3 className="font-display text-base font-semibold">
          Backfill Dealer Code on Migrated Vehicles
        </h3>
      </div>

      <div className="p-4 space-y-4 text-sm">
        <p className="text-ink-muted">
          Run this <strong>twice</strong> — once per TVS dealership. Pick the branches that
          belong to that dealership, enter the dealer code, preview, then apply.
        </p>

        {/* Branch picker */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              Branches
            </label>
            <button
              type="button"
              onClick={toggleAll}
              data-testid="backfill-toggle-all"
              className="font-mono text-[10px] uppercase tracking-wider text-ink-muted hover:text-ink"
            >
              {allSelected ? "Clear all" : "Select all"}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-48 overflow-y-auto border border-canvas-line bg-canvas p-2">
            {branches.length === 0 && (
              <div className="font-mono text-xs text-ink-subtle p-2">
                No branches found. Create branches first.
              </div>
            )}
            {branches.map((b) => {
              const checked = selectedBranchIds.has(b.id);
              return (
                <label
                  key={b.id}
                  className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-canvas-panel text-sm"
                  data-testid={`backfill-branch-${b.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(b.id)}
                    className="cursor-pointer"
                  />
                  <span className="font-mono text-[10px] text-ink-muted tabular-nums w-12">
                    {b.code}
                  </span>
                  <span>{b.name}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Dealer Code + only-unset */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Dealer Code (numeric)
            </label>
            <input
              type="number"
              value={dealerCode}
              onChange={(e) => setDealerCode(e.target.value)}
              placeholder="e.g., 12345"
              data-testid="backfill-dealer-code-input"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
            />
          </div>
          <div className="md:col-span-2 flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={onlyUnset}
                onChange={(e) => setOnlyUnset(e.target.checked)}
                data-testid="backfill-only-unset"
              />
              <span>Only update vehicles that don't yet have a dealer code</span>
            </label>
          </div>
        </div>

        {/* Status filter */}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Vehicle Status (optional · leave empty for all)
          </label>
          <div className="flex flex-wrap gap-3">
            {["available", "reserved", "sold"].map((s) => (
              <label
                key={s}
                className="flex items-center gap-2 text-sm cursor-pointer px-3 py-1.5 border border-canvas-line hover:border-ink"
                data-testid={`backfill-status-${s}`}
              >
                <input
                  type="checkbox"
                  checked={selectedStatuses.has(s)}
                  onChange={() => toggleStatus(s)}
                />
                <span className="capitalize">{s}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={runPreview}
            disabled={previewing || selectedBranchIds.size === 0}
            data-testid="backfill-preview-btn"
            className="px-3 py-2 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white disabled:opacity-50"
          >
            {previewing ? "Counting…" : "Preview Count"}
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={applying || !preview || preview.matched_count === 0 || !dealerCode}
            data-testid="backfill-apply-btn"
            className="px-3 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand disabled:opacity-50"
            title={!preview ? "Run preview first" : "Apply the dealer code"}
          >
            {applying ? "Applying…" : "Apply Backfill"}
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const { data } = await api.get("/vehicles/admin/dealer-code-diagnostic");
                setDiagnostic(data);
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            data-testid="backfill-diagnostic-btn"
            className="px-3 py-2 border border-canvas-line text-ink-muted text-xs hover:border-ink hover:text-ink"
            title="See how dealer_code is stored across statuses"
          >
            Show Distribution
          </button>
          <button
            type="button"
            onClick={async () => {
              setInferBusy(true);
              try {
                const { data } = await api.post("/vehicles/admin/infer-dealer-code/preview");
                setInferPreview(data);
                if (data.total_will_update === 0) {
                  toast.info("Nothing to infer — all branches already covered or no Sold history.");
                }
              } catch (err) {
                toast.error(formatApiError(err));
              } finally {
                setInferBusy(false);
              }
            }}
            data-testid="infer-preview-btn"
            className="px-3 py-2 border border-[#00B341] text-[#00713a] text-xs font-semibold hover:bg-[#00B341] hover:text-white disabled:opacity-50"
            title="Auto-infer dealer_code on Available vehicles from each branch's Sold history"
            disabled={inferBusy}
          >
            {inferBusy ? "Analysing…" : "Auto-Infer from Sold History"}
          </button>
        </div>

        {/* Auto-infer preview */}
        {inferPreview && (
          <div className="border border-[#00B341] bg-[#00B341]/5 p-3 space-y-3" data-testid="infer-preview-result">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                Auto-Infer Preview · {fmtNumber(inferPreview.total_will_update)} available vehicles will be tagged
                {inferPreview.total_unmatched > 0 && (
                  <span className="ml-2 text-destructive">
                    · {fmtNumber(inferPreview.total_unmatched)} in branches with no Sold history (skipped)
                  </span>
                )}
              </div>
              <button onClick={() => setInferPreview(null)} className="text-ink-muted hover:text-ink text-xs">close</button>
            </div>
            {inferPreview.branches.length > 0 ? (
              <table className="w-full text-xs">
                <thead className="bg-canvas-panel">
                  <tr>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Branch</th>
                    <th className="text-right px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Avail. to Tag</th>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Inferred Code</th>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Distinct in Branch</th>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {inferPreview.branches.map((b) => (
                    <tr key={b.branch_id} className="border-t border-canvas-line">
                      <td className="px-2 py-1">{b.branch_name}</td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums">{fmtNumber(b.available_to_tag)}</td>
                      <td className="px-2 py-1 font-mono">{b.inferred_code}</td>
                      <td className="px-2 py-1 font-mono text-[10px] text-ink-muted">{b.distinct_codes_in_branch.join(", ")}</td>
                      <td className="px-2 py-1">
                        <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${b.confidence === "single" ? "border-[#00B341] text-[#00713a]" : "border-[#B58900] text-[#B58900]"}`}>
                          {b.confidence}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-xs text-ink-muted">No branches qualify.</div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Tag ${inferPreview.total_will_update} available vehicles with branch-inferred dealer codes?`)) return;
                  setInferBusy(true);
                  try {
                    const { data } = await api.post("/vehicles/admin/infer-dealer-code");
                    toast.success(`Updated ${data.modified} vehicles across ${data.branches.length} branches`);
                    setInferPreview(null);
                  } catch (err) {
                    toast.error(formatApiError(err));
                  } finally {
                    setInferBusy(false);
                  }
                }}
                disabled={inferBusy || inferPreview.total_will_update === 0}
                data-testid="infer-apply-btn"
                className="px-3 py-2 bg-[#00B341] text-white text-xs font-semibold hover:bg-[#00713a] disabled:opacity-50"
              >
                {inferBusy ? "Applying…" : `Apply Auto-Infer (${fmtNumber(inferPreview.total_will_update)})`}
              </button>
            </div>
          </div>
        )}

        {/* Diagnostic */}
        {diagnostic && (
          <div className="border border-canvas-line bg-canvas-card p-3" data-testid="backfill-diagnostic-result">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                Dealer Code Distribution · {fmtNumber(diagnostic.total_vehicles)} total vehicles
              </div>
              <button onClick={() => setDiagnostic(null)} className="text-ink-muted hover:text-ink text-xs">close</button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-canvas-panel">
                <tr>
                  <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Status</th>
                  <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Has dealer_code?</th>
                  <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Stored as type</th>
                  <th className="text-right px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Count</th>
                  <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Sample value</th>
                </tr>
              </thead>
              <tbody>
                {diagnostic.buckets.map((b, i) => (
                  <tr key={i} className="border-t border-canvas-line">
                    <td className="px-2 py-1 capitalize">{b.status || "—"}</td>
                    <td className="px-2 py-1">
                      <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${b.has_dealer_code ? "border-[#00B341] text-[#00713a]" : "border-destructive text-destructive"}`}>
                        {b.has_dealer_code ? "yes" : "no"}
                      </span>
                    </td>
                    <td className="px-2 py-1 font-mono text-[10px]">{b.stored_as_type || "—"}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums">{fmtNumber(b.count)}</td>
                    <td className="px-2 py-1 font-mono text-[10px]">{b.sample_value === null || b.sample_value === undefined ? "—" : String(b.sample_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="border border-ink bg-canvas-card p-3" data-testid="backfill-preview-result">
            <div className="flex items-center gap-2 mb-2">
              <Tag size={14} weight="bold" />
              <span className="font-semibold">
                {fmtNumber(preview.matched_count)} vehicles will be updated
              </span>
            </div>
            {preview.by_branch?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs">
                {preview.by_branch.map((b) => (
                  <div
                    key={b.branch_id}
                    className="border border-canvas-line px-2 py-1 bg-canvas flex items-center justify-between"
                  >
                    <span className="font-mono text-[10px] text-ink-muted truncate">
                      {branchById[b.branch_id]?.name || b.branch_id}
                    </span>
                    <span className="font-mono tabular-nums font-semibold">{b.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Result */}
        {lastResult && (
          <div
            className="border border-[#00B341] bg-[#00B341]/5 p-3 flex items-center gap-2"
            data-testid="backfill-result"
          >
            <CheckCircle size={16} weight="bold" className="text-[#00B341]" />
            <span>
              <strong>{fmtNumber(lastResult.modified_count)}</strong> vehicles tagged with dealer
              code <strong>{lastResult.dealer_code}</strong>. Run again with the other dealership's
              branches if needed.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
